import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Chip } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { size } from 'global/lib/lodash';
import SnackAlerts from 'global/components/snack-alerts';
import { MAXSIZE } from '../drop-area';
import { DropRoot, DropText, PreviewGrid, PreviewThumb } from './styles';

/**
 * Replacement for the `DropzoneArea` from `@shotskydiver/material-ui-dropzone`
 * (which depended on the removed `@mui/styles`). Built on `react-dropzone`,
 * which is already a dependency.
 *
 * Preserves the subset of the original API actually used in this codebase:
 *   - onChange(files): called with the cumulative File[] after each drop
 *   - onAlert(message, level): callback for rejection messages (falls back to
 *     in-component SnackAlerts when not supplied)
 *   - acceptedFiles: shotskydiver-style array of mime types (e.g. ['image/*'])
 *   - filesLimit: max number of files
 *   - maxFileSize: bytes (defaults to MAXSIZE from drop-area)
 *   - dropzoneText: prompt text
 *   - showPreviewsInDropzone / showPreviews: render previews under the prompt
 *   - useChipsForPreview: render previews as MUI Chips instead of thumbnails
 *   - disabled
 *
 * `initialFiles` is intentionally ignored here — the form-level consumer
 * (global/components/form) renders initial files itself as chips outside the
 * dropzone.
 */
export const DropzoneArea = React.forwardRef((props, _ref) => {
  const {
    onChange,
    onAlert,
    acceptedFiles,
    accept,
    filesLimit,
    maxFileSize = MAXSIZE,
    dropzoneText = 'Drag and drop a file here or click',
    showPreviewsInDropzone = true,
    showPreviews,
    useChipsForPreview = false,
    disabled,
    // Props we accept but don't render — consumed by other parts of the form.
    initialFiles: _initialFiles,
    showAlerts: _showAlerts,
    previewGridProps: _previewGridProps,
    previewChipProps: _previewChipProps,
    ...rest
  } = props;

  const [files, setFiles] = useState([]);
  const [alerts, setAlerts] = useState();

  const onDrop = useCallback(
    (accepted, rejected) => {
      if (size(rejected)) {
        const tooBig = rejected.filter((r) => (r.errors || []).some((e) => e.code === 'file-too-large'));
        const message = size(tooBig) ? `${size(rejected)} file(s) failed to upload. File is too big.` : `${size(rejected)} file(s) failed to upload.`;
        if (onAlert) {
          onAlert(message, 'error');
        } else {
          setAlerts({ message, severity: 'error' });
        }
      }

      // When filesLimit === 1, treat the drop as a single-file replacement
      // rather than appending — otherwise dragging a new file would just be
      // rejected as "too many files" while leaving the old one in place.
      const isSingle = filesLimit === 1;
      const next = isSingle ? accepted.slice(0, 1) : [...files, ...accepted];
      setFiles(next);
      onChange?.(next);
    },
    [files, onChange, onAlert, filesLimit]
  );

  // shotskydiver took `acceptedFiles` as an array of mime-type strings.
  // react-dropzone v10 accepts the same shape (array or comma-separated).
  const acceptProp = accept ?? acceptedFiles;

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptProp,
    maxSize: maxFileSize,
    maxFiles: filesLimit,
    disabled,
  });

  const removeFile = (index) => {
    const next = files.filter((_, i) => i !== index);
    setFiles(next);
    onChange?.(next);
  };

  const renderPreview = (file, i) => {
    const key = `${file.name}-${i}`;
    if (useChipsForPreview) {
      return <Chip key={key} label={file.name} size="small" onDelete={() => removeFile(i)} />;
    }
    const isImage = file.type && file.type.startsWith('image/');
    if (!isImage) {
      return <Chip key={key} label={file.name} size="small" onDelete={() => removeFile(i)} />;
    }
    return (
      <PreviewThumb key={key} onClick={(e) => e.stopPropagation()}>
        <img src={URL.createObjectURL(file)} alt={file.name} />
      </PreviewThumb>
    );
  };

  const showThePreviews = (showPreviewsInDropzone || showPreviews) && size(files) > 0;

  return (
    <>
      <DropRoot {...getRootProps()} isDragActive={isDragActive} isDisabled={!!disabled} {...rest}>
        <input {...getInputProps()} />
        <CloudUploadIcon fontSize="large" color="action" />
        <DropText>{dropzoneText}</DropText>
        {showThePreviews && <PreviewGrid>{files.map(renderPreview)}</PreviewGrid>}
      </DropRoot>
      <SnackAlerts alerts={alerts} setAlerts={setAlerts} />
    </>
  );
});
